'use client';

import { QueryKeys } from '@/constants/query-keys';
import { AnalyticsEvent } from '@/enums/analytics-type';
import { watchListApiService } from '@/services/(frontend)/profile/watch-list-api.service';
import { WatchListContent } from '@/types/watch-list-movie';
import { markQueryDirty } from '@/utils/query-dirty-flag';
import { removeFromList } from '@/utils/query-update-utils';
import { WatchListMovie } from '@cinemark/composer-api-client';
import { PagedListOfWatchListItem, Profile, WatchListItem } from '@cinemark/profile-api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';
import { useIsAuthReady } from './is-auth-ready';

type AddOrRemoveWatchlistType = {
  analyticsEvent?: AnalyticsEvent;
  linkCategory?: string;
  movieId: number;
  movieTitle: string;
};

export type UpdateWatchlistWithAnalyticsType = {
  analyticsEvent?: AnalyticsEvent;
  isFavorite: boolean;
  linkCategory?: string;
  movieId: number;
  movieTitle: string;
  previousProfile?: Profile;
  previousWatchlist?: WatchListContent;
  previousWatchlistSimple?: PagedListOfWatchListItem;
  queryKey: readonly string[];
};

export const useWatchList = (): ReturnType<typeof useQuery<WatchListContent>> => {
  const updateQueryStaleTime = useDynamicStaleTime();
  const { isAuthenticated } = useIsAuthReady();

  return useQuery<WatchListContent>({
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await watchListApiService.getWatchList();

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.user.watchList, response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.user.watchList,
  });
};

export const useWatchListSimple = (): ReturnType<typeof useQuery<PagedListOfWatchListItem>> => {
  const updateQueryStaleTime = useDynamicStaleTime();
  const { isAuthenticated } = useIsAuthReady();

  return useQuery<PagedListOfWatchListItem>({
    enabled: isAuthenticated,
    queryFn: async () => {
      const response = await watchListApiService.getWatchListSimple();

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.user.watchListSimple, response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.user.watchListSimple,
  });
};

export const useAddWatchListMovie = (): ReturnType<typeof useMutation<void, unknown, AddOrRemoveWatchlistType, UpdateWatchlistWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ movieId }) => await watchListApiService.addWatchListMovie(movieId),
    onError: (_err, _newMovie, context) => {
      if (context?.previousWatchlist) {
        queryClient.setQueryData(QueryKeys.user.watchList, context.previousWatchlist);
      }
      if (context?.previousWatchlistSimple) {
        queryClient.setQueryData(QueryKeys.user.watchListSimple, context.previousWatchlistSimple);
      }
      if (context?.previousProfile) {
        queryClient.setQueryData(QueryKeys.user.profile, context.previousProfile);
      }
    },
    onMutate: async ({ analyticsEvent, linkCategory, movieId, movieTitle }) => {
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.watchList });
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.profile });

      const previousWatchlist = queryClient.getQueryData<WatchListContent>(QueryKeys.user.watchList);
      const previousWatchlistSimple = queryClient.getQueryData<PagedListOfWatchListItem>(QueryKeys.user.watchListSimple);
      const previousProfile = queryClient.getQueryData<Profile>(QueryKeys.user.profile);
      queryClient.setQueryData<Profile>(QueryKeys.user.profile, old => old ? { ...old, hasWatchListItems: true } : old);
      queryClient.setQueryData<WatchListContent>(QueryKeys.user.watchList, (old): WatchListContent | undefined => {
        if (!old) return old;

        const newMovie: WatchListMovie = {
          cta: 'Get Tickets',
          movieId,
          moviePosterUrl: '',
          movieTitle,
          movieUrl: '',
          showtimeInfo: '',
          sortOrder: 0,
          sortOrderTitle: '',
        };

        return {
          ...old,
          movies: [...old.movies, newMovie],
        };
      });

      queryClient.setQueryData<PagedListOfWatchListItem>(QueryKeys.user.watchListSimple, (old): PagedListOfWatchListItem | undefined => {
        if (!old) {
          return {
            data: [{
              auditDateCreated: new Date(),
              isListed: true,
              movieId,
              movieWatched: false,
              rank: undefined,
              watchedDate: undefined,
            }],
            hasNextPage: false,
            hasPreviousPage: false,
            pageCount: 1,
            pageNumber: 1,
            pageSize: 1,
            totalItemCount: 1,
          };
        }

        const newItem: WatchListItem = {
          auditDateCreated: new Date(),
          isListed: true,
          movieId,
          movieWatched: false,
          rank: undefined,
          watchedDate: undefined,
        };

        return {
          ...old,
          data: [...old.data, newItem],
          totalItemCount: (old.totalItemCount ?? old.data.length) + 1,
        };
      });

      return {
        analyticsEvent,
        isFavorite: true,
        linkCategory,
        movieId,
        movieTitle,
        previousProfile,
        previousWatchlist,
        previousWatchlistSimple,
        queryKey: QueryKeys.user.watchList,
      };
    },
    onSuccess: () => {
      markQueryDirty(queryClient, QueryKeys.user.watchList);
    },
  });
};

export const useRemoveWatchListMovie = (): ReturnType<typeof useMutation<void, unknown, AddOrRemoveWatchlistType, UpdateWatchlistWithAnalyticsType>> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ movieId }) => await watchListApiService.removeWatchListMovie(movieId),
    onError: (_err, _newMovie, context) => {
      if (context?.previousWatchlist) {
        queryClient.setQueryData(QueryKeys.user.watchList, context.previousWatchlist);
      }
      if (context?.previousWatchlistSimple) {
        queryClient.setQueryData(QueryKeys.user.watchListSimple, context.previousWatchlistSimple);
      }
      if (context?.previousProfile) {
        queryClient.setQueryData(QueryKeys.user.profile, context.previousProfile);
      }
    },
    onMutate: async ({ analyticsEvent, linkCategory, movieId, movieTitle }) => {
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.watchList });
      await queryClient.cancelQueries({ queryKey: QueryKeys.user.profile });
      const previousWatchlist = queryClient.getQueryData<WatchListContent>(QueryKeys.user.watchList);
      const previousWatchlistSimple = queryClient.getQueryData<PagedListOfWatchListItem>(QueryKeys.user.watchListSimple);
      const previousProfile = queryClient.getQueryData<Profile>(QueryKeys.user.profile);
      const hasRemovedLastMovie = previousWatchlistSimple
        ? (previousWatchlistSimple.totalItemCount ?? previousWatchlistSimple.data.length) === 1
          && previousWatchlistSimple.data[0]?.movieId === movieId
        : previousWatchlist?.movies.length === 1 && previousWatchlist.movies[0]?.movieId === movieId;

      if (hasRemovedLastMovie) {
        queryClient.setQueryData<Profile>(QueryKeys.user.profile, old => old ? { ...old, hasWatchListItems: false } : old);
      }

      queryClient.setQueryData<WatchListContent>(QueryKeys.user.watchList, old =>
        removeFromList(old, movie => movie.movieId === movieId, 'movies')
      );

      queryClient.setQueryData<PagedListOfWatchListItem>(QueryKeys.user.watchListSimple, (old): PagedListOfWatchListItem | undefined => {
        if (!old) return old;
        const filtered = old.data.filter(item => item.movieId !== movieId);
        const existed = filtered.length !== old.data.length;
        return {
          ...old,
          data: filtered,
          totalItemCount: existed ? (old.totalItemCount ?? old.data.length) - 1 : old.totalItemCount ?? old.data.length,
        };
      });

      return {
        analyticsEvent,
        isFavorite: false,
        linkCategory,
        movieId,
        movieTitle,
        previousProfile,
        previousWatchlist,
        previousWatchlistSimple,
        queryKey: QueryKeys.user.watchList,
      };
    },
    onSuccess: () => {
      markQueryDirty(queryClient, QueryKeys.user.watchList);
    },
  });
};
